import {
    Fragment, ReactNode,
} from 'react'
import {
    GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import {
    ColumnConfigMappingType,
} from 'app/shared-components/SkyBaseDataGrid/SkyBaseDataGrid.types'

import {
    LanesTableRowUniq,
} from 'app/Apps/Pricing/ContractBasis/utils/createQuote.types'
import List from 'app/common/DomainObject/ComponentMapper/List'

import {
    WithId,
} from 'app/types/common.types'

import isoStrToLocalStr from 'app/utils/date/isoStrToLocalStr'

const ABSENCE_SYMBOL = '-'

type AdditionalFeesTable = WithId<number> & {
  title: string,
  price: string,
  description: string,
}

export const finalSelectionAdditionalFeesColumns: ColumnConfigMappingType<AdditionalFeesTable>[] = [
    {
        name: 'title',
        labelKey: 'FEE',
        width: 70,
        sorting: false,
    },
    {
        name: 'price',
        labelKey: 'AMOUNT',
        width: 35,
        sorting: false,
    },
    {
        name: 'description',
        labelKey: 'DESCRIPTION',
        sorting: false,
    },
]

type LaneTableType = LanesTableRowUniq & {
  validFrom: string,
  baseLeaseDays: string,
  containerProductTypes: string,
  handoverDeliveryLtl: string,
  baseLease: string,
  collectionDropOff: string,
  cargoStraps: string,
  damageWaiver: string,
  deliveryFtlVan: string,
  deliveryFtlTruck: string,
}

const renderMultilineValues = (values: readonly ReactNode[]) => {
    return (
        <>
            {values.map((value, index) => {
                return (
                // eslint-disable-next-line react/no-array-index-key
                    <Fragment key={index}>
                        {value}
                        <br />
                    </Fragment>
                )
            })}
        </>
    )
}

export const finalSelectionLaneFeesColumns: ColumnConfigMappingType<LaneTableType>[] = [
    {
        name: 'laneNumber',
        labelKey: 'LANE_SLASH_FEE',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            const {
                laneNumber, laneFees,
            } = params.row

            return (
                <>
                    {laneNumber}
                    <br />
                    {laneFees.laneFeesNumber}
                </>
            )
        },
        sorting: false,
    },
    {
        name: 'validFrom',
        labelKey: 'VALID_FROM',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            const {
                laneFees,
            } = params.row

            return (
                <>
                    {isoStrToLocalStr(laneFees.validFrom)}
                    {laneFees.validTo && (
                        <>
                            <br />
                            {isoStrToLocalStr(laneFees.validTo)}
                        </>
                    )}
                </>
            )
        },
        sorting: false,
    },
    {
        name: 'originAirportIataCode',
        labelKey: 'FROM',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            return (
                <List value={[params.row.originAirportIataCode]} />
            )
        },
        sorting: false,
    },
    {
        name: 'destAirportIataCode',
        labelKey: 'TO',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            return (
                <List value={[params.row.destAirportIataCode]} />
            )
        },
        sorting: false,
    },
    {
        name: 'expPalletsYear',
        labelKey: 'PALLETS',
        sorting: false,
    },
    {
        name: 'baseLeaseDays',
        labelKey: 'BASE_LEASE_DAYS',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            const {
                laneFees,
            } = params.row

            return <>{laneFees.baseLeaseDays ? String(laneFees.baseLeaseDays) : ABSENCE_SYMBOL}</>
        },
        sorting: false,
    },
    {
        name: 'containerProductTypes',
        labelKey: 'CONTAINER_TYPE',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            return renderMultilineValues(params.row.laneFees?.containerProductTypes || [])
        },
        sorting: false,
    },
    {
        name: 'skyCellDelivers',
        labelKey: 'SKYCELL_DELIVERS',
        sorting: false,
    },
    {
        name: 'skyCellCollects',
        labelKey: 'SKYCELL_COLLECTS',
        sorting: false,
    },
    {
        name: 'handoverDeliveryLtl',
        labelKey: 'HANDOVER_DELIVERY_LTL',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            return renderMultilineValues(params.row.laneFees?.handoverDeliveryLtl || [])
        },
        sorting: false,
    },
    {
        name: 'baseLease',
        labelKey: 'BASE_LEASE',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            return renderMultilineValues(params.row.laneFees?.baseLease || [])
        },
        sorting: false,
    },
    {
        name: 'collectionDropOff',
        labelKey: 'COLLECTION_DROP_OFF',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            return renderMultilineValues(params.row.laneFees?.collectionDropOff || [])
        },
        sorting: false,
    },
    {
        name: 'cargoStraps',
        labelKey: 'CARGO_STRAPS',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            return renderMultilineValues(params.row.laneFees?.cargoStraps || [])
        },
        sorting: false,
    },
    {
        name: 'damageWaiver',
        labelKey: 'DAMAGE_WAIVER',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            return renderMultilineValues(params.row.laneFees?.damageWaiver || [])
        },
        sorting: false,
    },
    {
        name: 'deliveryFtlVan',
        labelKey: 'DELIVERY_FTL_VAN_TRUCK',
        renderCell: (params: GridRenderCellParams<LanesTableRowUniq>) => {
            const {
                laneFees,
            } = params.row

            return <>{`${laneFees.deliveryFtlVan || ABSENCE_SYMBOL} / ${laneFees.deliveryFtlTruck || ABSENCE_SYMBOL}`}</>
        },
        sorting: false,
    },
]
